import { useState, useRef, useEffect } from "react";
import { IonFooter } from "@ionic/react";
import { Send, X, Camera, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessage } from "@/shared/utils/errors";
import type { PhotoFile } from "@/shared/utils/photo";
import { uploadPhoto } from "@/shared/utils/photoUpload";
import { takePhoto, selectPhotoFromLibrary } from "@/shared/utils/photo";

export interface SendMessageRequest {
  body: string;
  photos?: PhotoFile[];
  photoUrl?: string; // Public URL of the uploaded photo (singular for now)
}

interface MessageComposerProps {
  onSendMessage: (message: SendMessageRequest) => Promise<void>;
  isPending?: boolean;
  placeholder?: string;
  taskId?: number | null; // Optional taskId for photo uploads
}

export function MessageComposer({ 
  onSendMessage, 
  isPending = false, 
  placeholder = "Enter message",
  taskId
}: MessageComposerProps) {
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  const handleTakePhoto = async () => {
    if (photos.length > 0) {
      toast({
        title: "Photo already selected",
        description: "Remove the current photo before taking a new one.",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingPhoto(true);
    try {
      const photo = await takePhoto();
      if (photo) {
        setPhotos([photo]);
      }
    } catch (error) {
      toast({
        title: "Failed to take photo",
        description: error instanceof Error ? error.message : "An error occurred while taking the photo",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPhoto(false);
    }
  };

  const handleSelectFromGallery = async () => {
    if (photos.length > 0) {
      toast({
        title: "Photo already selected",
        description: "Remove the current photo before selecting a new one.",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingPhoto(true);
    try {
      const photo = await selectPhotoFromLibrary();
      if (photo) {
        setPhotos([photo]);
      }
    } catch (error) {
      toast({
        title: "Failed to select photo",
        description: error instanceof Error ? error.message : "An error occurred while selecting the photo",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Allow sending if there's either text or photos
    if (!message.trim() && photos.length === 0) return;

    setIsUploading(true);
    try {
      let photoUrl: string | undefined;

      // Upload photo first if any (single photo for now)
      if (photos.length > 0) {
        try {
          photoUrl = await uploadPhoto(photos[0], taskId);
        } catch (uploadError) {
          toast({
            title: "Failed to upload photo",
            description: extractErrorMessage(uploadError),
            variant: "destructive",
          });
          setIsUploading(false);
          return;
        }
      }

      // Send message with photo URL
      await onSendMessage({ 
        body: message.trim(), 
        photoUrl
      });
      setMessage("");
      setPhotos([]);
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (error) {
      toast({
        title: "Failed to send message",
        description: extractErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const isDisabled = (!message.trim() && photos.length === 0) || isPending || isUploading;

  return (
    <IonFooter className="">
      <div className="px-safe border-t border-border mx-2 py-2" data-testid="message-composer">
        <form onSubmit={handleSubmit}>
          <div className="space-y-3">
            {/* Single photo preview - shown inline above textarea */}
            {photos.length > 0 && (
              <div className="relative inline-block">
                <img
                  src={photos[0].dataUrl}
                  alt="Selected photo"
                  className="h-32 w-auto rounded-lg border border-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => setPhotos([])}
                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm"
                  aria-label="Remove photo"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Textarea
                  ref={textareaRef}
                  placeholder={placeholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  className="min-h-[44px] max-h-32 resize-none px-4 py-3 rounded-3xl border border-border bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  data-testid="textarea-message"
                />
              </div>
              
              {/* Camera and Plus icons - only show if no photo selected */}
              {photos.length === 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleTakePhoto}
                    disabled={isLoadingPhoto || isUploading}
                    className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
                    aria-label="Take photo"
                  >
                    <Camera className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectFromGallery}
                    disabled={isLoadingPhoto || isUploading}
                    className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
                    aria-label="Select from gallery"
                  >
                    <Plus className="h-6 w-6" />
                  </button>
                </>
              )}
              
              <Button
                type="submit"
                disabled={isDisabled}
                className="p-2 shrink-0 h-11 w-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                data-testid="button-send"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </form>
      </div>
    </IonFooter>
  );
}
